import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../lib/AuthContext";

// "Paciente activo": para quién opera el titular (él mismo o un familiar).
// id === SELF significa el propio titular. Persistido en localStorage por usuario
// (para no heredar el familiar de otra cuenta en el mismo navegador) y
// sincronizado entre componentes vía un evento propio.
// Ver CLAUDE.md → "Perfiles gestionados / familiares".

const KEY_PREFIX = "binapsis.acting-patient";
const EVENT = "acting-patient-change";

export const SELF = "self";
export const SELF_PATIENT = { id: SELF, name: "" };

const storageKey = (userId) => `${KEY_PREFIX}:${userId || "anon"}`;

function readActing(userId) {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return SELF_PATIENT;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.id === "string") return parsed;
    return SELF_PATIENT;
  } catch {
    return SELF_PATIENT;
  }
}

export default function useActingPatient() {
  const { user } = useAuth();
  const userId = user?.id;
  const [acting, setActingState] = useState(() => readActing(userId));

  useEffect(() => {
    setActingState(readActing(userId));
    const handler = () => setActingState(readActing(userId));
    window.addEventListener(EVENT, handler);
    window.addEventListener("storage", handler); // otras pestañas
    return () => {
      window.removeEventListener(EVENT, handler);
      window.removeEventListener("storage", handler);
    };
  }, [userId]);

  const setActing = useCallback(
    (next) => {
      const value =
        !next || next.id === SELF
          ? SELF_PATIENT
          : { id: next.id, name: next.name || "" };
      localStorage.setItem(storageKey(userId), JSON.stringify(value));
      window.dispatchEvent(new Event(EVENT));
    },
    [userId]
  );

  const resetToSelf = useCallback(() => {
    localStorage.setItem(storageKey(userId), JSON.stringify(SELF_PATIENT));
    window.dispatchEvent(new Event(EVENT));
  }, [userId]);

  return { acting, setActing, resetToSelf, isSelf: acting.id === SELF };
}
